import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Toast } from "primereact/toast";
import {
  FaYoutube,
  FaFacebookSquare,
  FaSearch,
  FaTimes,
  FaSyncAlt,
  FaUsers,
  FaComments,
  FaSitemap,
  FaList,
  FaCrown,
  FaUserPlus,
  FaWallet,
  FaBoxOpen,
  FaUserFriends,
  FaInbox,
  FaCopy,
  FaChevronDown,
  FaChevronRight,
  FaLayerGroup,
  FaExclamationTriangle,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaPercent,
} from "react-icons/fa";
import { useBaseUrl } from "../Contexts/BaseUrlContext";
import { useStateContext } from "../Contexts/ContextProvider";

/* ================= styles ================= */

const CSS = `
@keyframes glFadeUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: none; } }
@keyframes glFade { from { opacity: 0; } to { opacity: 1; } }
@keyframes glBar { from { width: 0; } }
@keyframes glPulse { 0% { box-shadow: 0 0 0 0 rgba(255,255,255,.45); } 70% { box-shadow: 0 0 0 12px rgba(255,255,255,0); } 100% { box-shadow: 0 0 0 0 rgba(255,255,255,0); } }
@keyframes glDraw { from { transform: scaleY(0); } to { transform: scaleY(1); } }
.gl-in { animation: glFadeUp .5s cubic-bezier(.2,.7,.2,1) both; }
.gl-fade { animation: glFade .25s ease both; }
.gl-bar { animation: glBar .9s cubic-bezier(.2,.7,.2,1) both; }
.gl-hub { animation: glPulse 2.4s ease-out infinite; }
.gl-line { transform-origin: top; animation: glDraw .6s ease both; }
.gl-press { transition: transform .15s ease, background-color .2s ease, border-color .2s ease, color .2s ease, box-shadow .2s ease; }
.gl-press:hover { transform: translateY(-1px); }
.gl-press:active { transform: scale(.96); }
.gl-branch { transition: transform .2s ease, box-shadow .25s ease, border-color .2s ease; }
.gl-branch:hover { transform: translateX(4px); box-shadow: 0 10px 24px -16px rgba(15,23,42,.4); }
.gl-mark { background: #fde68a; color: inherit; border-radius: 3px; padding: 0 1px; }
@media (prefers-reduced-motion: reduce) {
  .gl-in, .gl-fade, .gl-bar, .gl-hub, .gl-line { animation: none; }
  .gl-press:hover, .gl-press:active, .gl-branch:hover { transform: none; }
}
`;

const CARD = "bg-white dark:bg-secondary-dark-bg rounded-2xl border border-slate-200 dark:border-slate-700";
const INPUT =
  "rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500";

const THEME = {
  youtube: {
    name: "YouTube",
    icon: FaYoutube,
    hero: "from-red-600 via-red-600 to-rose-700",
    soft: "bg-red-50 dark:bg-red-900/20",
    border: "border-red-200 dark:border-red-900/50",
    text: "text-red-600 dark:text-red-400",
    bar: "bg-red-500",
    line: "bg-red-200 dark:bg-red-900/60",
    dot: "bg-red-500",
    chip: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
  },
  facebook: {
    name: "Facebook",
    icon: FaFacebookSquare,
    hero: "from-blue-600 via-blue-600 to-indigo-700",
    soft: "bg-blue-50 dark:bg-blue-900/20",
    border: "border-blue-200 dark:border-blue-900/50",
    text: "text-blue-600 dark:text-blue-400",
    bar: "bg-blue-500",
    line: "bg-blue-200 dark:bg-blue-900/60",
    dot: "bg-blue-500",
    chip: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  },
};

/* ================= helpers ================= */

const fmt = (n) => Number(n || 0).toLocaleString("en-IN");
const parseAny = (s) => {
  if (!s) return null;
  const d = new Date(String(s).replace(" ", "T"));
  return Number.isNaN(d.getTime()) ? null : d;
};
const fmtDate = (d) => (d ? d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "-");
const fmtTime = (d) => (d ? d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "");
const plat = (g) => (String(g.platform || "").toLowerCase() === "facebook" ? "facebook" : "youtube");
const isMaster = (g) => String(g.grp_type || "").toLowerCase() === "master" || Number(g.group_id) === 1 || Number(g.group_id) === 2;
const members = (g) => Number(g.total_members || 0);
const creatorName = (g) => (g.creator_name && String(g.creator_name).trim() ? g.creator_name : null);

const Hi = ({ text, q }) => {
  const s = String(text ?? "");
  if (!q || !s) return <>{s}</>;
  const i = s.toLowerCase().indexOf(q.toLowerCase());
  if (i < 0) return <>{s}</>;
  return (
    <>
      {s.slice(0, i)}
      <mark className="gl-mark">{s.slice(i, i + q.length)}</mark>
      {s.slice(i + q.length)}
    </>
  );
};

/* ================= page ================= */

const GroupList = () => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState("network"); // network | table
  const [query, setQuery] = useState("");
  const [platform, setPlatform] = useState("all");
  const [sizeFilter, setSizeFilter] = useState("all"); // all | empty | active
  const [sortBy, setSortBy] = useState("members");
  const [tableSort, setTableSort] = useState({ field: "total_members", dir: "desc" });
  const [open, setOpen] = useState({ youtube: true, facebook: true });

  const toast = useRef(null);
  const searchRef = useRef(null);
  const navigate = useNavigate();
  const { baseUrl } = useBaseUrl();
  const { currentMode } = useStateContext();

  /* ----- API (same action) ----- */

  const fetchGroups = useCallback(async () => {
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("action", "groupListWithMembers");
      const res = await fetch(`${baseUrl}/group.php`, { method: "POST", body: fd, credentials: "include" });
      const data = await res.json();
      setGroups(Array.isArray(data?.groups) ? data.groups : []);
    } catch (err) {
      console.error("API Error:", err);
      toast.current?.show({ severity: "error", summary: "Error", detail: "Could not load groups" });
    }
    setLoading(false);
  }, [baseUrl]);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  useEffect(() => {
    const onKey = (e) => {
      const typing = ["INPUT", "TEXTAREA", "SELECT"].includes(document.activeElement?.tagName);
      if (!typing && e.key === "/") {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  /* ----- derived ----- */

  const q = query.trim();

  const byPlatform = useMemo(() => {
    const out = {};
    ["youtube", "facebook"].forEach((p) => {
      const list = groups.filter((g) => plat(g) === p);
      const master = list.find(isMaster) || null;
      const subs = list.filter((g) => !isMaster(g));
      const subMembers = subs.reduce((s, g) => s + members(g), 0);
      out[p] = {
        master,
        subs,
        subMembers,
        empty: subs.filter((g) => members(g) === 0).length,
        biggest: [...subs].sort((a, b) => members(b) - members(a))[0] || null,
        maxSub: Math.max(1, ...subs.map(members)),
      };
    });
    return out;
  }, [groups]);

  const matches = useCallback(
    (g) => {
      if (platform !== "all" && plat(g) !== platform) return false;
      if (sizeFilter === "empty" && members(g) !== 0) return false;
      if (sizeFilter === "active" && members(g) === 0) return false;
      if (!q) return true;
      const ql = q.toLowerCase();
      return [g.grp_name, g.group_id, creatorName(g), g.created_by].map((v) => String(v ?? "").toLowerCase()).some((v) => v.includes(ql));
    },
    [platform, sizeFilter, q]
  );

  const sortSubs = useCallback(
    (list) =>
      [...list].sort((a, b) => {
        if (sortBy === "members") return members(b) - members(a);
        if (sortBy === "name") return String(a.grp_name || "").localeCompare(String(b.grp_name || ""));
        const da = parseAny(a.date_time)?.getTime() || 0;
        const db = parseAny(b.date_time)?.getTime() || 0;
        return sortBy === "oldest" ? da - db : db - da;
      }),
    [sortBy]
  );

  const tableRows = useMemo(() => {
    const dir = tableSort.dir === "asc" ? 1 : -1;
    return groups.filter(matches).sort((a, b) => {
      let x = a[tableSort.field];
      let y = b[tableSort.field];
      if (tableSort.field === "total_members") {
        x = members(a);
        y = members(b);
      } else if (tableSort.field === "date_time") {
        x = parseAny(a.date_time)?.getTime() || 0;
        y = parseAny(b.date_time)?.getTime() || 0;
      } else {
        x = String(x ?? "").toLowerCase();
        y = String(y ?? "").toLowerCase();
      }
      if (x < y) return -1 * dir;
      if (x > y) return 1 * dir;
      return 0;
    });
  }, [groups, matches, tableSort]);

  const totals = useMemo(
    () => ({
      groups: groups.length,
      subs: groups.filter((g) => !isMaster(g)).length,
      empty: groups.filter((g) => !isMaster(g) && members(g) === 0).length,
      memberships: groups.filter((g) => !isMaster(g)).reduce((s, g) => s + members(g), 0),
    }),
    [groups]
  );

  const hasFilters = q || platform !== "all" || sizeFilter !== "all";
  const clearFilters = () => {
    setQuery("");
    setPlatform("all");
    setSizeFilter("all");
  };

  const copyText = (text, label = "Copied") => {
    navigator.clipboard?.writeText(String(text));
    toast.current?.show({ severity: "info", summary: label, detail: String(text), life: 1500 });
  };

  const goMembers = (e, id) => {
    e.stopPropagation();
    navigate(`/group/members/${id}`);
  };
  const goChat = (e, id) => {
    e.stopPropagation();
    navigate(`/group-chat/${id}`);
  };

  /* ----- pieces ----- */

  const MasterHub = ({ p }) => {
    const T = THEME[p];
    const Icon = T.icon;
    const info = byPlatform[p];
    const m = info.master;
    return (
      <div className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${T.hero} text-white p-5 sm:p-6`}>
        <div className="pointer-events-none absolute -top-16 -right-10 w-56 h-56 rounded-full bg-white/10 blur-2xl" />
        <div className="relative flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0">
            <span className="gl-hub w-14 h-14 shrink-0 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center">
              <Icon size={26} />
            </span>
            <div className="min-w-0">
              <p className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-white/80">
                <FaCrown size={10} /> Master group
              </p>
              <h2 className="text-xl sm:text-2xl font-bold truncate">{m?.grp_name || `YCNP ${T.name} Monetization`}</h2>
              {m && (
                <button type="button" onClick={() => copyText(m.group_id, "Group ID copied")} className="text-xs text-white/70 hover:text-white inline-flex items-center gap-1">
                  ID {m.group_id} <FaCopy size={9} />
                </button>
              )}
            </div>
          </div>
          {m && (
            <div className="flex gap-2">
              <button type="button" onClick={(e) => goMembers(e, m.group_id)} className="gl-press inline-flex items-center gap-2 h-9 px-3.5 rounded-xl bg-white text-slate-900 text-sm font-semibold shadow">
                <FaUsers size={12} /> Members
              </button>
              <button type="button" onClick={(e) => goChat(e, m.group_id)} className="gl-press inline-flex items-center gap-2 h-9 px-3.5 rounded-xl bg-white/15 hover:bg-white/25 text-sm font-semibold">
                <FaComments size={12} /> Chat
              </button>
            </div>
          )}
        </div>

        <div className="relative mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: "Total Members", value: members(m) },
            { label: "Sub Groups", value: info.subs.length },
            { label: "Members in sub groups", value: info.subMembers },
            { label: "Empty sub groups", value: info.empty },
          ].map((s) => (
            <div key={s.label} className="rounded-xl bg-white/10 border border-white/10 px-3 py-2.5">
              <p className="text-2xl font-bold tabular-nums">{loading && !groups.length ? "–" : fmt(s.value)}</p>
              <p className="text-[11px] text-white/75">{s.label}</p>
            </div>
          ))}
        </div>

        {m && (
          <div className="relative mt-4 flex flex-wrap gap-2 text-xs">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/15">
              <FaPercent size={9} /> Referral {m.reffral_comission ?? 0}
            </span>
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/15">
              <FaPercent size={9} /> Creator {m.creator_comission ?? 0}
            </span>
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/15">Since {fmtDate(parseAny(m.date_time))}</span>
          </div>
        )}
      </div>
    );
  };

  const Branch = ({ g, p, i }) => {
    const T = THEME[p];
    const n = members(g);
    const d = parseAny(g.date_time);
    const isBiggest = byPlatform[p].biggest?.group_id === g.group_id && n > 0;
    return (
      <li className="relative pl-8 gl-in" style={{ animationDelay: `${Math.min(i, 14) * 40}ms` }}>
        {/* connector */}
        <span className={`absolute left-3 top-0 bottom-0 w-0.5 ${T.line}`} />
        <span className={`absolute left-3 top-7 w-5 h-0.5 ${T.line}`} />
        <span className={`absolute left-[7px] top-[23px] w-2.5 h-2.5 rounded-full ring-4 ring-white dark:ring-slate-800 ${n ? T.dot : "bg-slate-300"}`} />

        <div
          role="button"
          tabIndex={0}
          onClick={() => navigate(`/group-chat/${g.group_id}`)}
          onKeyDown={(e) => e.key === "Enter" && navigate(`/group-chat/${g.group_id}`)}
          className={`gl-branch cursor-pointer rounded-xl border bg-white dark:bg-slate-800 p-3.5 ${n === 0 ? "border-dashed border-slate-300 dark:border-slate-600" : "border-slate-200 dark:border-slate-700"}`}
        >
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-[180px]">
              <div className="flex items-center gap-2">
                <p className="font-semibold text-slate-900 dark:text-white truncate">
                  <Hi text={g.grp_name || "Unnamed group"} q={q} />
                </p>
                {isBiggest && (
                  <span className={`shrink-0 inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded ${T.chip}`}>
                    <FaCrown size={8} /> Biggest
                  </span>
                )}
                {n === 0 && <span className="shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 dark:bg-slate-700">Empty</span>}
              </div>
              <p className="text-xs text-slate-500 mt-0.5">
                By <b className="text-slate-700 dark:text-slate-200 font-medium">{creatorName(g) ? <Hi text={creatorName(g)} q={q} /> : "Unknown creator"}</b>
                <span className="mx-1.5">•</span>
                <span className="tabular-nums">
                  ID <Hi text={g.group_id} q={q} />
                </span>
                <span className="mx-1.5">•</span>
                {fmtDate(d)}
              </p>
            </div>

            <div className="w-36">
              <div className="flex items-baseline justify-between">
                <span className="text-lg font-bold text-slate-900 dark:text-white tabular-nums">{fmt(n)}</span>
                <span className="text-[11px] text-slate-500">members</span>
              </div>
              <div className="h-1.5 rounded-full bg-slate-100 dark:bg-slate-700 overflow-hidden">
                <div className={`gl-bar h-full rounded-full ${T.bar}`} style={{ width: `${(n / byPlatform[p].maxSub) * 100}%` }} />
              </div>
            </div>

            <div className="hidden md:flex flex-col gap-1 text-[11px] text-slate-500 w-24">
              <span>Referral <b className="text-slate-700 dark:text-slate-200">{g.reffral_comission ?? 0}</b></span>
              <span>Creator <b className="text-slate-700 dark:text-slate-200">{g.creator_comission ?? 0}</b></span>
            </div>

            <div className="flex gap-1.5">
              <button type="button" onClick={(e) => goMembers(e, g.group_id)} className="gl-press inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-slate-900 text-white dark:bg-white dark:text-slate-900 text-xs font-semibold">
                <FaUsers size={10} /> Members
              </button>
              <button type="button" onClick={(e) => goChat(e, g.group_id)} className="gl-press w-8 h-8 rounded-lg border border-slate-200 dark:border-slate-600 flex items-center justify-center text-slate-600 dark:text-slate-300" title="Open chat" aria-label="Open chat">
                <FaComments size={11} />
              </button>
            </div>
          </div>
        </div>
      </li>
    );
  };

  const PlatformSection = ({ p, delay }) => {
    const T = THEME[p];
    const info = byPlatform[p];
    const subs = sortSubs(info.subs.filter(matches));
    const masterHidden = q && info.master && !matches(info.master) && subs.length === 0;
    if (platform !== "all" && platform !== p) return null;
    if (masterHidden) return null;
    return (
      <section className="gl-in" style={{ animationDelay: `${delay}ms` }}>
        <MasterHub p={p} />
        <div className={`mt-3 rounded-2xl border ${T.border} ${T.soft} p-3 sm:p-4`}>
          <button type="button" onClick={() => setOpen((o) => ({ ...o, [p]: !o[p] }))} className="w-full flex items-center gap-2 text-left">
            {open[p] ? <FaChevronDown size={11} className={T.text} /> : <FaChevronRight size={11} className={T.text} />}
            <span className="text-sm font-semibold text-slate-900 dark:text-white">Sub groups</span>
            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${T.chip}`}>
              {subs.length}
              {subs.length !== info.subs.length ? ` of ${info.subs.length}` : ""}
            </span>
            <span className="ml-auto text-xs text-slate-500">Everyone who joins a sub group is also added to the master group</span>
          </button>
          {open[p] && (
            <ul className="mt-3 space-y-2">
              {subs.map((g, i) => (
                <Branch key={g.group_id} g={g} p={p} i={i} />
              ))}
              {!subs.length && <li className="text-sm text-slate-500 py-6 text-center">{q ? `No ${T.name} sub group matches "${q}"` : "No sub groups here"}</li>}
            </ul>
          )}
        </div>
      </section>
    );
  };

  const SortTh = ({ field, children, right }) => {
    const active = tableSort.field === field;
    const Icon = !active ? FaSort : tableSort.dir === "asc" ? FaSortUp : FaSortDown;
    return (
      <th className={`px-3 py-3 font-medium whitespace-nowrap ${right ? "text-right" : "text-left"}`}>
        <button
          type="button"
          onClick={() => setTableSort((s) => ({ field, dir: s.field === field && s.dir === "desc" ? "asc" : "desc" }))}
          className="inline-flex items-center gap-1.5 hover:text-slate-900 dark:hover:text-white"
        >
          {children}
          <Icon size={10} className={active ? "text-cyan-600" : "text-slate-300"} />
        </button>
      </th>
    );
  };

  const quickLinks = [
    { label: "Joining Request", to: "/group/join-request", icon: FaUserPlus },
    { label: "All Members", to: "/group/all-members", icon: FaUserFriends },
    { label: "Member Wallet", to: "/group/wallet", icon: FaWallet },
    { label: "Packages", to: "/group-packages", icon: FaBoxOpen },
  ];

  /* ================= render ================= */

  return (
    <div style={currentMode === "Dark" ? { backgroundColor: "#20232A" } : {}} className="w-full p-4 sm:p-6 lg:p-8 bg-slate-50 min-h-screen">
      <style>{CSS}</style>
      <Toast ref={toast} />

      {/* Header */}
      <div className="gl-in flex flex-wrap items-end justify-between gap-4 mb-5">
        <div>
          <p className="text-sm text-slate-500 dark:text-slate-400">Group</p>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">Group Management</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">YouTube and Facebook monetization groups with every sub group under them.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {quickLinks.map((l) => {
            const Icon = l.icon;
            return (
              <button key={l.to} type="button" onClick={() => navigate(l.to)} className="gl-press inline-flex items-center gap-2 h-9 px-3 rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-secondary-dark-bg text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50">
                <Icon size={12} /> {l.label}
              </button>
            );
          })}
          <button type="button" onClick={fetchGroups} disabled={loading} className="gl-press inline-flex items-center gap-2 h-9 px-4 rounded-xl bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-semibold disabled:opacity-60">
            <FaSyncAlt size={12} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { label: "All groups", value: totals.groups, icon: FaLayerGroup, tint: "bg-cyan-50 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300", onClick: () => setSizeFilter("all") },
          { label: "Sub groups", value: totals.subs, icon: FaSitemap, tint: "bg-violet-50 text-violet-600 dark:bg-violet-900/30 dark:text-violet-300", onClick: () => setView("network") },
          { label: "Members in sub groups", value: totals.memberships, icon: FaUsers, tint: "bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-300", onClick: () => setSizeFilter("active") },
          { label: "Empty sub groups", value: totals.empty, icon: FaExclamationTriangle, tint: "bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-300", onClick: () => setSizeFilter("empty") },
        ].map((c, i) => {
          const Icon = c.icon;
          return (
            <button key={c.label} type="button" onClick={c.onClick} className={`${CARD} gl-in gl-press p-4 flex items-center gap-3 text-left`} style={{ animationDelay: `${i * 60}ms` }}>
              <span className={`w-10 h-10 rounded-xl flex items-center justify-center ${c.tint}`}>
                <Icon />
              </span>
              <div>
                <p className="text-xl font-bold text-slate-900 dark:text-white tabular-nums">{fmt(c.value)}</p>
                <p className="text-xs text-slate-500">{c.label}</p>
              </div>
            </button>
          );
        })}
      </div>

      {/* Toolbar */}
      <div className={`${CARD} gl-in mt-4 p-3 flex flex-wrap items-center gap-3`} style={{ animationDelay: "240ms" }}>
        <div className="relative flex-1 min-w-[240px]">
          <FaSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={13} />
          <input ref={searchRef} value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by group name, group ID or creator" className={`${INPUT} w-full h-10 pl-10 pr-10`} />
          <div className="absolute right-2.5 top-1/2 -translate-y-1/2">
            {query ? (
              <button type="button" onClick={() => setQuery("")} className="p-1.5 text-slate-400 hover:text-slate-700" aria-label="Clear search">
                <FaTimes size={12} />
              </button>
            ) : (
              <kbd className="hidden sm:inline text-xs text-slate-400 border border-slate-200 dark:border-slate-600 rounded px-1.5 py-0.5">/</kbd>
            )}
          </div>
        </div>
        <div className="inline-flex p-1 rounded-xl bg-slate-100 dark:bg-slate-700">
          {[
            { id: "all", label: "Both" },
            { id: "youtube", label: "YouTube", icon: FaYoutube, cls: "text-red-600" },
            { id: "facebook", label: "Facebook", icon: FaFacebookSquare, cls: "text-blue-600" },
          ].map((t) => {
            const Icon = t.icon;
            return (
              <button key={t.id} type="button" onClick={() => setPlatform(t.id)} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all active:scale-95 ${platform === t.id ? "bg-white dark:bg-secondary-dark-bg text-slate-900 dark:text-white shadow-sm" : "text-slate-500 dark:text-slate-300"}`}>
                {Icon && <Icon size={12} className={t.cls} />} {t.label}
              </button>
            );
          })}
        </div>
        <select value={sizeFilter} onChange={(e) => setSizeFilter(e.target.value)} className={`${INPUT} h-10 px-3`}>
          <option value="all">Any size</option>
          <option value="active">Has members</option>
          <option value="empty">Empty only</option>
        </select>
        {view === "network" && (
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className={`${INPUT} h-10 px-3`}>
            <option value="members">Most members</option>
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
            <option value="name">Name A–Z</option>
          </select>
        )}
        <div className="inline-flex p-1 rounded-xl bg-slate-100 dark:bg-slate-700">
          {[
            { id: "network", icon: FaSitemap, label: "Network" },
            { id: "table", icon: FaList, label: "Table" },
          ].map((v) => {
            const Icon = v.icon;
            return (
              <button key={v.id} type="button" onClick={() => setView(v.id)} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all active:scale-95 ${view === v.id ? "bg-white dark:bg-secondary-dark-bg text-slate-900 dark:text-white shadow-sm" : "text-slate-500 dark:text-slate-300"}`}>
                <Icon size={11} /> {v.label}
              </button>
            );
          })}
        </div>
        {hasFilters && (
          <button type="button" onClick={clearFilters} className="text-sm font-medium text-cyan-700 dark:text-cyan-400 hover:underline">
            Clear all
          </button>
        )}
      </div>

      {/* Content */}
      <div className="mt-4">
        {loading && !groups.length ? (
          <div className="grid grid-cols-1 2xl:grid-cols-2 gap-5">
            {[0, 1].map((i) => (
              <div key={i} className="space-y-3">
                <div className="h-48 rounded-2xl bg-slate-200 dark:bg-slate-700 animate-pulse" />
                <div className="h-16 rounded-xl bg-slate-200 dark:bg-slate-700 animate-pulse" />
                <div className="h-16 rounded-xl bg-slate-200 dark:bg-slate-700 animate-pulse" />
              </div>
            ))}
          </div>
        ) : !groups.length ? (
          <div className={`${CARD} px-5 py-16 text-center`}>
            <FaInbox className="mx-auto text-slate-300" size={36} />
            <p className="mt-3 font-semibold text-slate-700 dark:text-slate-200">No groups found</p>
          </div>
        ) : view === "network" ? (
          <div className={`grid grid-cols-1 ${platform === "all" ? "2xl:grid-cols-2" : ""} gap-5 items-start`}>
            {PlatformSection({ p: "youtube", delay: 280 })}
            {PlatformSection({ p: "facebook", delay: 360 })}
          </div>
        ) : (
          <div className={`${CARD} overflow-hidden`}>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700">
                  <tr>
                    <SortTh field="grp_name">Group Name</SortTh>
                    <SortTh field="grp_type">Type</SortTh>
                    <SortTh field="platform">Platform</SortTh>
                    <SortTh field="creator_name">Created By</SortTh>
                    <SortTh field="total_members" right>Members</SortTh>
                    <th className="px-3 py-3 font-medium text-right">Referral</th>
                    <th className="px-3 py-3 font-medium text-right">Creator</th>
                    <SortTh field="date_time">Date / Time</SortTh>
                    <th className="px-3 py-3 pr-4 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                  {tableRows.map((g) => {
                    const p = plat(g);
                    const T = THEME[p];
                    const Icon = T.icon;
                    const d = parseAny(g.date_time);
                    return (
                      <tr key={g.group_id} onClick={() => navigate(`/group-chat/${g.group_id}`)} className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/40 transition-colors">
                        <td className="px-3 py-3 pl-4">
                          <p className="font-medium text-slate-900 dark:text-white">
                            <Hi text={g.grp_name || "Unnamed group"} q={q} />
                          </p>
                          <p className="text-xs text-slate-500 tabular-nums">
                            ID <Hi text={g.group_id} q={q} />
                          </p>
                        </td>
                        <td className="px-3 py-3">
                          {isMaster(g) ? (
                            <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                              <FaCrown size={9} /> Master
                            </span>
                          ) : (
                            <span className="text-xs font-semibold px-2 py-1 rounded-full bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300">Sub</span>
                          )}
                        </td>
                        <td className="px-3 py-3">
                          <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-1 rounded-full ${T.chip}`}>
                            <Icon size={10} /> {T.name}
                          </span>
                        </td>
                        <td className="px-3 py-3 text-slate-700 dark:text-slate-200">{creatorName(g) ? <Hi text={creatorName(g)} q={q} /> : <span className="text-slate-400">Unknown</span>}</td>
                        <td className="px-3 py-3 text-right font-semibold tabular-nums text-slate-900 dark:text-white">{fmt(members(g))}</td>
                        <td className="px-3 py-3 text-right tabular-nums">{g.reffral_comission ?? 0}</td>
                        <td className="px-3 py-3 text-right tabular-nums">{g.creator_comission ?? 0}</td>
                        <td className="px-3 py-3 whitespace-nowrap">
                          <div className="text-slate-700 dark:text-slate-200">{fmtDate(d)}</div>
                          <div className="text-xs text-slate-500">{fmtTime(d)}</div>
                        </td>
                        <td className="px-3 py-3 pr-4">
                          <div className="flex justify-end gap-1.5">
                            <button type="button" onClick={(e) => goMembers(e, g.group_id)} className="gl-press inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-slate-900 text-white dark:bg-white dark:text-slate-900 text-xs font-semibold">
                              <FaUsers size={10} /> Members
                            </button>
                            <button type="button" onClick={(e) => goChat(e, g.group_id)} className="gl-press w-8 h-8 rounded-lg border border-slate-200 dark:border-slate-600 flex items-center justify-center text-slate-600 dark:text-slate-300" aria-label="Open chat">
                              <FaComments size={11} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {!tableRows.length && (
                    <tr>
                      <td colSpan={9} className="px-4 py-12 text-center text-slate-500">
                        No group matches these filters
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GroupList;